import {useEffect} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {atomButtonDesigns, atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomImage from '@s-ui/react-atom-image'
import AtomTooltip, {AtomTooltipPlacements} from '@s-ui/react-atom-tooltip'
import {useSuiContext} from '@s-ui/react-context'
import MoleculeDrawer, {moleculeDrawerPlacements, moleculeDrawerSizes} from '@s-ui/react-molecule-drawer'
import PrimitiveTypography, {primitiveTypographyDesign} from '@s-ui/react-primitive-typography'
import NetChatHistoryList from '@adv-mt/mt-net-chat-history-list'
import IconArrowLeft from '@adv-mt/mt-svg-icons/lib/ArrowLeft.js'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconNoteEditOutline from '@adv-mt/mt-svg-icons/lib/NoteEditOutline.js'
import IconWindowSidebarOutline from '@adv-mt/mt-svg-icons/lib/WindowSidebarOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {ROUTES} from '../../../config/index.js'

const BASE_CLASS = 'mt-Net-chatsSidebar'

const ChatsSidebar = ({
  isOpen,
  setIsOpen,
  onNewChat,
  chats,
  currentChatId,
  onSelectChat,
  disabled,
  isHigherThanMobileResolution,
  isReady
}) => {
  const {domain, i18n} = useSuiContext()
  const {page} = useAnalyticsTrack()

  const config = domain.get('config')
  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const {NET_LOGO_EXTENDED_URL: netLogoUrl, NET_MENU_TRACKING_PAGE_NAME} = config.get('COMMON').AI_ASSISTANT

  useEffect(() => {
    if (!isOpen) return
    domain
      .get('get_page_tracking_data_use_case')
      .execute({page: NET_MENU_TRACKING_PAGE_NAME})
      .then(response => {
        if (response) page(response)
      })
  }, [isOpen])

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleNewChat = () => {
    setIsOpen(false)
    onNewChat()
  }

  return (
    <aside className={BASE_CLASS}>
      {/* Collapsed sidebar */}
      {isHigherThanMobileResolution && (
        <div className={`${BASE_CLASS}-collapsed`}>
          <AtomTooltip
            placement={AtomTooltipPlacements.RIGHT}
            content={<span>{i18n.t('NET.SIDE_MENU.ARIA_LABEL_OPEN_MENU')}</span>}
          >
            <AtomButton
              design={atomButtonDesigns.FLAT}
              aria-label={i18n.t('NET.SIDE_MENU.ARIA_LABEL_OPEN_MENU')}
              leftIcon={<IconWindowSidebarOutline size={atomIconSizes.medium} />}
              onClick={handleToggle}
              isFitted
            />
          </AtomTooltip>
          <AtomTooltip
            placement={AtomTooltipPlacements.RIGHT}
            content={<span>{i18n.t('NET.SIDE_MENU.ARIA_LABEL_NEW_CHAT')}</span>}
          >
            <AtomButton
              design={atomButtonDesigns.OUTLINE}
              shape={atomButtonShapes.CIRCULAR}
              size={atomButtonSizes.LARGE}
              aria-label={i18n.t('NET.SIDE_MENU.ARIA_LABEL_NEW_CHAT')}
              leftIcon={<IconNoteEditOutline size={atomIconSizes.medium} />}
              onClick={handleNewChat}
              disabled={disabled}
              isFitted
            />
          </AtomTooltip>
        </div>
      )}

      {/* Expanded sidebar drawer */}
      {isReady && (
        <MoleculeDrawer
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          placement={moleculeDrawerPlacements.LEFT}
          size={moleculeDrawerSizes.M}
        >
          {isOpen && (
            <div className={`${BASE_CLASS}-expanded`}>
              {isHigherThanMobileResolution ? (
                <div className={`${BASE_CLASS}-header`}>
                  <AtomImage
                    src={`${urlStaticsImages}${netLogoUrl}`}
                    className={`${BASE_CLASS}-headerLogo`}
                    alt="Net AI assistant"
                  />
                  <AtomTooltip
                    placement={AtomTooltipPlacements.LEFT}
                    content={<span>{i18n.t('NET.SIDE_MENU.ARIA_LABEL_CLOSE_MENU')}</span>}
                  >
                    <AtomButton
                      design={atomButtonDesigns.FLAT}
                      aria-label={i18n.t('NET.SIDE_MENU.ARIA_LABEL_CLOSE_MENU')}
                      leftIcon={<IconWindowSidebarOutline size={atomIconSizes.medium} />}
                      onClick={handleToggle}
                    />
                  </AtomTooltip>
                </div>
              ) : (
                <div className={`${BASE_CLASS}-header ${BASE_CLASS}-headerMobile`}>
                  <AtomButton
                    design={atomButtonDesigns.FLAT}
                    aria-label={i18n.t('NET.SIDE_MENU.ARIA_LABEL_CLOSE_MENU')}
                    leftIcon={<IconArrowLeft size={atomIconSizes.medium} />}
                    onClick={handleToggle}
                  />
                  <PrimitiveTypography design={primitiveTypographyDesign.HEADLINE_2} className={`${BASE_CLASS}-title`}>
                    {i18n.t('NET.SIDE_MENU.ARIA_LABEL_CHAT_HISTORY')}
                  </PrimitiveTypography>
                  <AtomButton
                    link
                    href={ROUTES.ADS_LIST_SEARCH}
                    leftIcon={<IconClose size={atomIconSizes.medium} />}
                    aria-label={i18n.t('NET.CLOSE_CHAT')}
                    design={atomButtonDesigns.FLAT}
                    className={`${BASE_CLASS}-headerCloseIcon`}
                    onClick={() => setIsOpen(false)}
                  />
                </div>
              )}

              <NetChatHistoryList
                chats={chats}
                currentChatId={currentChatId}
                onSelectChat={chatId => {
                  if (!isHigherThanMobileResolution) {
                    setIsOpen(false)
                  }
                  onSelectChat(chatId)
                }}
                onNewChat={handleNewChat}
              />
            </div>
          )}
        </MoleculeDrawer>
      )}
    </aside>
  )
}

ChatsSidebar.displayName = 'ChatsSidebar'
ChatsSidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  chats: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string, title: PropTypes.string})),
  currentChatId: PropTypes.string,
  onSelectChat: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isHigherThanMobileResolution: PropTypes.bool,
  isReady: PropTypes.bool
}

export default ChatsSidebar
